import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  X,
  Minus,
  Send,
  Bot,
  User,
  Sparkles,
  Phone,
  Mail,
  Building,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content: `Hi! I'm the HOASP AI Assistant. I can help you explore our IT solutions, website and application development services, e-commerce solutions, and IT maintenance services.\n\nHow can I help you today?`,
  timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  suggestedActions: [
    "I need a website",
    "I need an e-commerce website",
    "I need a web application",
    "I need a mobile app",
    "I need IT maintenance",
    "I have another IT requirement",
    "Talk to HOASP"
  ]
};

// Safe controlled text & link formatter (XSS-safe, no dangerouslySetInnerHTML)
function FormattedMessage({ text }) {
  if (!text) return null;

  // Split lines
  const lines = text.split('\n');

  return (
    <div className="space-y-1.5 text-sm leading-relaxed select-text">
      {lines.map((line, idx) => {
        // Bullet list check
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          return (
            <div key={idx} className="flex items-start gap-2 pl-1 text-gray-200">
              <span className="text-cyan-400 mt-1.5 text-xs font-bold">•</span>
              <span>{renderInlineText(line.replace(/^[-*]\s+/, ''))}</span>
            </div>
          );
        }
        if (line.trim().match(/^\d+\.\s+/)) {
          const num = line.match(/^\d+\./)[0];
          return (
            <div key={idx} className="flex items-start gap-2 pl-1 text-gray-200">
              <span className="text-cyan-400 text-xs font-semibold">{num}</span>
              <span>{renderInlineText(line.replace(/^\d+\.\s+/, ''))}</span>
            </div>
          );
        }
        if (!line.trim()) {
          return <div key={idx} className="h-1.5" />;
        }
        return (
          <p key={idx} className="text-gray-200">
            {renderInlineText(line)}
          </p>
        );
      })}
    </div>
  );
}

function renderInlineText(text) {
  // Simple bold highlighting without raw HTML
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    organizationType: 'Company',
    requirement: '',
    timeline: 'Within 1 Month',
    budget: ''
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [messages, isOpen, isMinimized, showLeadModal]);

  const handleSendMessage = async (textToSend) => {
    const text = (textToSend || inputValue).trim();
    if (!text || isLoading) return;

    // Check if user clicked "Talk to HOASP" or "Submit Enquiry"
    if (text.toLowerCase() === 'talk to hoasp' || text.toLowerCase() === 'submit enquiry' || text.toLowerCase() === 'request a callback') {
      setShowLeadModal(true);
      return;
    }

    const userMsg = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Determine API endpoint base
      const endpoint = process.env.REACT_APP_BACKEND_URL 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/chat` 
        : '/api/chat';

      // Send recent context history (last 6 messages)
      const historyContext = messages.slice(-6).map((m) => ({
        role: m.role,
        content: m.content
      }));

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: historyContext
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        const botMsg = {
          id: `bot_${Date.now()}`,
          role: 'assistant',
          content: data.reply,
          suggestedActions: data.suggestedActions || [],
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages((prev) => [...prev, botMsg]);
      } else {
        const fallbackText = data.reply || "You're sending messages too quickly. Please wait a moment and try again.";
        setMessages((prev) => [
          ...prev,
          {
            id: `err_${Date.now()}`,
            role: 'assistant',
            content: fallbackText,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            suggestedActions: ["Submit Enquiry", "Talk to HOASP"]
          }
        ]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: 'assistant',
          content: "Sorry, I'm having trouble responding right now. Please try again or submit an enquiry to the HOASP team.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          suggestedActions: ["Submit Enquiry", "Talk to HOASP"]
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || (!leadForm.email && !leadForm.phone) || !leadForm.requirement) {
      toast.error('Please fill in your name, contact detail, and brief requirement.');
      return;
    }

    setLeadSubmitting(true);
    try {
      const endpoint = process.env.REACT_APP_BACKEND_URL 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/leads` 
        : '/api/leads';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadForm,
          chatHistory: messages.map(m => ({ role: m.role, content: m.content }))
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setLeadSubmitted(true);
        toast.success('Enquiry Sent Successfully!', {
          description: 'A HOASP IT specialist will get in touch shortly.'
        });
        
        // Add confirmation message to chat stream
        setMessages(prev => [
          ...prev,
          {
            id: `lead_confirm_${Date.now()}`,
            role: 'assistant',
            content: `Thank you, **${leadForm.name}**! Your requirement has been securely submitted to our engineering team. We will contact you at **${leadForm.phone || leadForm.email}** shortly.`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);

        setTimeout(() => {
          setShowLeadModal(false);
          setLeadSubmitted(false);
          setLeadForm({
            name: '',
            email: '',
            phone: '',
            organization: '',
            organizationType: 'Company',
            requirement: '',
            timeline: 'Within 1 Month',
            budget: ''
          });
        }, 2200);
      } else {
        toast.error(data.message || 'Submission failed. Please call us at +91 8796 118 101.');
      }
    } catch (err) {
      console.error('Lead submit error:', err);
      toast.error('Network error. Please email us directly at info@hoasp.com');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const resetConversation = () => {
    setMessages([WELCOME_MESSAGE]);
    toast.info('Conversation reset.');
  };

  return (
    <>
      {/* Floating Action Trigger Button */}
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            setIsMinimized(false);
          }}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-full shadow-2xl shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105 active:scale-95 transition-all duration-300 group border border-cyan-400/30 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          aria-label="Open HOASP AI Assistant"
        >
          <div className="relative">
            <Bot className="w-5 h-5 text-white animate-pulse" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-gray-950 animate-ping" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-gray-950" />
          </div>
          <span className="font-semibold text-sm tracking-wide hidden sm:inline">
            Ask HOASP AI
          </span>
          <span className="text-xs bg-cyan-400/20 text-cyan-200 px-2 py-0.5 rounded-full font-mono border border-cyan-300/20">
            Online
          </span>
        </button>
      )}

      {/* Chat Window Container */}
      {isOpen && (
        <div
          className={`fixed z-50 transition-all duration-300 ease-out flex flex-col ${
            isMinimized
              ? 'bottom-5 right-5 w-72 h-14 bg-gray-900 border border-cyan-500/30 rounded-2xl shadow-2xl overflow-hidden'
              : 'bottom-4 right-4 sm:bottom-5 sm:right-5 w-[calc(100vw-2rem)] sm:w-[420px] max-w-[420px] h-[580px] max-h-[calc(100vh-2rem)] bg-gray-950/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/80 overflow-hidden'
          }`}
          role="dialog"
          aria-label="HOASP AI Chatbot Window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900/90 border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="relative w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md">
                <Bot className="w-5 h-5 text-white" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border border-gray-900" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="font-bold text-sm text-white tracking-tight">HOASP Assistant</h3>
                  <span className="text-[10px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.2 rounded font-medium border border-cyan-500/30">
                    AI
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Verified IT Solutions & Support
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {!isMinimized && (
                <button
                  onClick={resetConversation}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  title="Reset Conversation"
                  aria-label="Reset Conversation"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title={isMinimized ? "Expand" : "Minimize"}
                aria-label="Minimize or Expand Chat"
              >
                {isMinimized ? <Minus className="w-4 h-4 rotate-90" /> : <Minus className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-gray-400 hover:text-rose-400 hover:bg-white/5 rounded-lg transition-colors"
                title="Close"
                aria-label="Close Chat Window"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body Area (Hidden when minimized) */}
          {!isMinimized && (
            <div className="flex-1 flex flex-col min-h-0 bg-gray-950/60 relative">
              {/* In-Chat Lead Capture Form Modal */}
              {showLeadModal && (
                <div className="absolute inset-0 z-20 bg-gray-950/95 backdrop-blur-md p-4 overflow-y-auto flex flex-col">
                  <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-cyan-400" />
                      <h4 className="font-semibold text-sm text-white">Connect with HOASP Experts</h4>
                    </div>
                    <button
                      onClick={() => setShowLeadModal(false)}
                      className="text-gray-400 hover:text-white p-1 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {leadSubmitted ? (
                    <div className="my-auto text-center py-8">
                      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3 animate-bounce" />
                      <h5 className="text-white font-bold text-base mb-1">Enquiry Received!</h5>
                      <p className="text-xs text-gray-300 max-w-xs mx-auto">
                        Thank you. A HOASP technical representative will review your project and get in touch with you shortly.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleLeadSubmit} className="space-y-3 text-xs flex-1 flex flex-col">
                      <div>
                        <label className="block text-gray-300 mb-1 font-medium">Your Name *</label>
                        <input
                          type="text"
                          required
                          value={leadForm.name}
                          onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                          placeholder="e.g. Rahul Sharma"
                          className="w-full bg-gray-900 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-gray-300 mb-1 font-medium">Phone Number *</label>
                          <input
                            type="tel"
                            value={leadForm.phone}
                            onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                            placeholder="+91 98765 43210"
                            className="w-full bg-gray-900 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 mb-1 font-medium">Email Address</label>
                          <input
                            type="email"
                            value={leadForm.email}
                            onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                            placeholder="rahul@example.com"
                            className="w-full bg-gray-900 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-gray-300 mb-1 font-medium">Organization / Company</label>
                          <input
                            type="text"
                            value={leadForm.organization}
                            onChange={(e) => setLeadForm({ ...leadForm, organization: e.target.value })}
                            placeholder="e.g. Apex Public School"
                            className="w-full bg-gray-900 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 mb-1 font-medium">Type</label>
                          <select
                            value={leadForm.organizationType}
                            onChange={(e) => setLeadForm({ ...leadForm, organizationType: e.target.value })}
                            className="w-full bg-gray-900 border border-white/15 rounded-lg px-2.5 py-2 text-white focus:outline-none focus:border-cyan-400"
                          >
                            <option value="Company">Business / Corporate</option>
                            <option value="School / College">School / College</option>
                            <option value="Hotel / Hospitality">Hotel / Hospitality</option>
                            <option value="Office">Office / Workplace</option>
                            <option value="Healthcare">Hospital / Clinic</option>
                            <option value="Startup">Startup / Individual</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-300 mb-1 font-medium">Requirement Details *</label>
                        <textarea
                          rows="3"
                          required
                          value={leadForm.requirement}
                          onChange={(e) => setLeadForm({ ...leadForm, requirement: e.target.value })}
                          placeholder="Please describe your website, app, or IT maintenance requirement..."
                          className="w-full bg-gray-900 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 resize-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-gray-300 mb-1 font-medium">Timeline</label>
                          <select
                            value={leadForm.timeline}
                            onChange={(e) => setLeadForm({ ...leadForm, timeline: e.target.value })}
                            className="w-full bg-gray-900 border border-white/15 rounded-lg px-2.5 py-2 text-white focus:outline-none focus:border-cyan-400"
                          >
                            <option value="Immediate / Emergency">Immediate / Urgent</option>
                            <option value="Within 1 Month">Within 1 Month</option>
                            <option value="1-3 Months">1-3 Months</option>
                            <option value="Planning / Exploring">Exploring / Planning</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-gray-300 mb-1 font-medium">Approx Budget (Optional)</label>
                          <input
                            type="text"
                            value={leadForm.budget}
                            onChange={(e) => setLeadForm({ ...leadForm, budget: e.target.value })}
                            placeholder="Optional"
                            className="w-full bg-gray-900 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                      </div>

                      <p className="text-[10px] text-gray-400 leading-tight pt-1">
                        🔒 Data Privacy: HOASP uses your details strictly to provide consultations and quotations. Never shared with third parties.
                      </p>

                      <div className="mt-auto pt-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setShowLeadModal(false)}
                          className="px-3 py-1.5 text-gray-300 hover:text-white rounded-lg hover:bg-white/5 transition"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={leadSubmitting}
                          className="px-4 py-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-medium rounded-lg shadow-md transition disabled:opacity-50"
                        >
                          {leadSubmitting ? 'Sending...' : 'Submit Request'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Chat Messages List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`flex gap-2 max-w-[88%] ${
                        msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                      }`}
                    >
                      {/* Avatar */}
                      <div
                        className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-1 text-[11px] ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-sm'
                        }`}
                      >
                        {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                      </div>

                      {/* Bubble */}
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 shadow-md ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-tr-none'
                            : 'bg-gray-900/90 border border-white/10 text-gray-200 rounded-tl-none'
                        }`}
                      >
                        <FormattedMessage text={msg.content} />
                        <span className="block text-[10px] text-gray-400 mt-1 text-right">
                          {msg.timestamp}
                        </span>
                      </div>
                    </div>

                    {/* Suggested Action Buttons */}
                    {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 ml-8 max-w-[90%]">
                        {msg.suggestedActions.map((action, actionIdx) => (
                          <button
                            key={actionIdx}
                            onClick={() => handleSendMessage(action)}
                            disabled={isLoading}
                            className="text-xs bg-cyan-950/70 hover:bg-cyan-900 text-cyan-300 border border-cyan-500/30 hover:border-cyan-400 rounded-full px-2.5 py-1 transition-all active:scale-95 disabled:opacity-50 text-left"
                          >
                            {action}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex items-center gap-2 text-gray-400 text-xs pl-8">
                    <div className="flex gap-1 items-center bg-gray-900 border border-white/10 rounded-full px-3 py-1.5">
                      <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" />
                      <span className="ml-1 text-[11px] text-gray-300">HOASP AI is thinking...</span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Quick Human Action Bar */}
              <div className="px-3 py-1.5 bg-gray-900/40 border-t border-white/5 flex items-center justify-between text-[11px]">
                <button
                  onClick={() => setShowLeadModal(true)}
                  className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 font-medium"
                >
                  <Sparkles className="w-3 h-3" />
                  Request Callback / Quotation
                </button>
                <a
                  href="https://wa.me/918796118101"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-medium"
                >
                  <Phone className="w-3 h-3" />
                  WhatsApp
                </a>
              </div>

              {/* Chat Input Box */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="p-3 bg-gray-900/90 border-t border-white/10 flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about website, e-commerce, IT support..."
                  disabled={isLoading}
                  maxLength={1000}
                  className="flex-1 bg-gray-950 border border-white/15 focus:border-cyan-400 rounded-xl px-3.5 py-2 text-xs text-white placeholder-gray-500 focus:outline-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="p-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  aria-label="Send Message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
